<template>
  <div v-if="show" :class="containerClass">
    <div class="modal-container">
      <div class="modal-content">
        <div class="modal-header">
          <div class="alert-icon" :class="iconColorClass">
            <FontAwesomeIcon :icon="modalIcon" :size="'2x'" :class="iconClass" />
          </div>
          <h2>{{ title }}</h2>
        </div>

        <slot name="content" />

        <div class="modal-footer">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faExclamationTriangle, faCircleExclamation } from '@fortawesome/free-solid-svg-icons'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: ''
  },
  type: {
    type: String,
    default: 'primary'
  },
  isFullscreen: {
    type: Boolean,
    default: false
  }
})

const containerClass = computed(() => ({
  'modal-overlay': !props.isFullscreen,
  'error-screen': props.isFullscreen,
  'danger-screen': props.isFullscreen && props.type === 'danger',
  'warning-screen': props.isFullscreen && props.type === 'warning'
}))

const iconColorClass = computed(() => ({
  warning: props.type === 'warning',
  danger: props.type === 'danger',
  primary: props.type === 'primary'
}))

const modalIcon = computed(() => {
  return props.type === 'warning' ? faCircleExclamation : faExclamationTriangle
})

const iconClass = computed(() => ({
  'warning-icon': props.type === 'warning',
  'danger-icon': props.type === 'danger',
  'primary-icon': props.type === 'primary'
}))
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.error-screen {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $white;
  z-index: 1000;

  &.danger-screen {
    background-color: rgba($danger, 0.05);
  }

  &.warning-screen {
    background-color: rgba($warning, 0.05);
  }
}

.modal-container {
  background-color: $white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  padding: 2rem;
  box-shadow: $uiShadow;
}

.modal-content {
  text-align: center;
}

.modal-header {
  margin-bottom: 1.5rem;

  h2 {
    color: $dark;
    margin-top: 1rem;
    font-size: 1.5rem;
    font-weight: bold;
  }
}

.alert-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;

  &.warning {
    background-color: rgba($warning, 0.2);
    .warning-icon {
      color: $warning;
    }
  }

  &.danger {
    background-color: rgba($danger, 0.2);
    .danger-icon {
      color: $danger;
    }
  }

  &.primary {
    background-color: rgba($primary, 0.2);
    .primary-icon {
      color: $primary;
    }
  }
}

.modal-footer {
  margin-top: 2rem;
}
</style>
